import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroupDirective, FormGroup, ControlContainer, ReactiveFormsModule } from '@angular/forms';
import { TrafficDeviationForm } from '@components/traffic-deviations/interfaces/traffic-deviation-form';
import { TrafficDeviationService } from '@components/traffic-deviations/services/traffic-deviation.service';
import { TypedForm } from '@modules/shared/interfaces/typed-form/typed-form';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { DetailInputComponent } from './components/detail-input/detail-input.component';

@Component({
    selector: 'app-information-details',
    templateUrl: './information-details.component.html',
    styleUrls: ['./information-details.component.scss'],
    imports: [
        CommonModule, AsyncPipe, ReactiveFormsModule, TranslateModule,
        DetailInputComponent
    ],
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InformationDetailsComponent implements OnInit, OnDestroy {
  @Input() requiredCustomControls$: BehaviorSubject<string[]>;
  @Input() editView: false;

  private readonly _formGroupDirective = inject(FormGroupDirective);
  private readonly _deviationService = inject(TrafficDeviationService);

  protected trafficDeviationAffectedGroup: FormGroup<TypedForm<TrafficDeviationForm['affected']>>;
  protected trafficDeviationInformationGroup: FormGroup<TypedForm<TrafficDeviationForm['trafficDeviationInformation']>>;
  protected readonly deviationType$ = this._deviationService.deviationType$;

  get getterStaticCustoms() {  
    return this.trafficDeviationInformationGroup?.controls.custom.controls[0]?.controls || null; 
  }
  get getterCustoms() { 
    return this.trafficDeviationInformationGroup?.controls.custom.controls[1]?.controls || null; 
  }
  get currentTemplateId(): string {
    return this.trafficDeviationInformationGroup?.controls?.templateId?.value || '';
  }

  private _parentForm: FormGroup;
  private readonly _destroy$ = new Subject<void>();

  ngOnInit(): void {
    this._parentForm = this._formGroupDirective.form.parent as FormGroup;

    this.trafficDeviationAffectedGroup = this._parentForm?.controls.affected as FormGroup<TypedForm<TrafficDeviationForm['affected']>>;
    this.trafficDeviationInformationGroup = this._parentForm?.controls.trafficDeviationInformation as FormGroup<TypedForm<TrafficDeviationForm['trafficDeviationInformation']>>;

    const currentTemplateId = this.trafficDeviationInformationGroup?.controls.templateId;
    currentTemplateId?.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntil(this._destroy$))
    .subscribe((templateId: string) => {
      this._deviationService.resetRequiredValidators();
      if (templateId) this._resetAllCustomControls();
    });
  }

  ngOnDestroy(): void {
    if (this._parentForm) {
      this._deviationService.resetRequiredValidators();
    }
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _resetAllCustomControls(): void {
    const customControls = this.trafficDeviationInformationGroup?.controls.custom.controls[0]?.controls;
    if (!customControls) return;
    const allTouched = Object.values(customControls).every(c => c.touched);
    for (const key in customControls) customControls[key].reset('');
    if (allTouched) {
      for (const key in customControls) customControls[key].markAsTouched();
    }
  }
}